<template>
  <div class="fu-ls" :class="{ 'fu-ls--busy': busy }" data-test="fu-list-summary">
    <section class="fu-ls__card" data-test="fu-list-worked">
      <h2 class="fu-ls__label">Worked so far</h2>
      <template v-if="busy">
        <span class="fu-ls__sk fu-ls__sk--num" aria-hidden="true"></span>
        <span class="fu-ls__sk fu-ls__sk--bar" aria-hidden="true"></span>
      </template>
      <template v-else>
        <div class="fu-ls__value">
          <span class="fu-ls__num" data-test="fu-list-worked-count">{{ s.worked }}</span>
          <span class="fu-ls__unit" data-test="fu-list-worked-total">of {{ s.total }} {{ s.total === 1 ? noun : `${noun}s` }}</span>
        </div>
        <div
          class="fu-ls__progress"
          role="progressbar"
          :aria-label="`Worked so far: ${s.worked} of ${s.total} ${noun}s have at least one follow-up attempt`"
          aria-valuemin="0"
          :aria-valuemax="s.total"
          :aria-valuenow="s.worked"
        >
          <div class="fu-ls__progress-fill" :style="{ width: `${s.workedPct}%` }"></div>
        </div>
        <div class="fu-ls__sub">At least one follow-up attempt</div>
      </template>
    </section>

    <section
      v-for="card in cards"
      :key="card.key"
      class="fu-ls__card"
      :data-test="`fu-list-${card.key}`"
    >
      <h2 class="fu-ls__label">{{ card.title }}</h2>
      <template v-if="busy">
        <span class="fu-ls__sk fu-ls__sk--bar" aria-hidden="true"></span>
        <span class="fu-ls__sk fu-ls__sk--line" aria-hidden="true"></span>
        <span class="fu-ls__sk fu-ls__sk--line" aria-hidden="true"></span>
      </template>
      <template v-else>
        <!-- The bar is a picture of the legend below it; the legend (with counts) is the text. -->
        <div class="fu-ls__stack" role="img" :aria-label="card.text" :data-test="`fu-list-${card.key}-bar`">
          <span
            v-for="seg in card.items"
            :key="seg.label"
            class="fu-ls__seg"
            :class="seg.cls"
            :style="{ flexGrow: seg.count }"
            :data-count="seg.count"
            aria-hidden="true"
          ></span>
        </div>
        <ul v-if="card.items.length" class="fu-ls__legend">
          <li v-for="seg in card.items" :key="seg.label" class="fu-ls__item" :data-test="`fu-list-${card.key}-item`">
            <span class="fu-ls__dot" :class="seg.cls" aria-hidden="true"></span>
            <span class="fu-ls__item-label">{{ seg.label }}</span>
            <span class="fu-ls__item-count">{{ seg.count }}</span>
          </li>
        </ul>
        <div v-else class="fu-ls__empty">No {{ noun }}s on this list.</div>
      </template>
    </section>
  </div>
</template>

<script>
/**
 * The three cards at the top of a saved-list page (the export's lines 141–167): "Worked so far"
 * (how many on the list have at least one attempt), "Follow-up attempts" (a stacked bar, 0 / 1 / 2 /
 * 3 or more) and "Outcome" (a stacked bar by status label, coloured by status category). They
 * replace the old list pages' two donut charts in the redesign's branch only.
 *
 * Presentational: the page passes the rows it already has, nothing is fetched or emitted. The
 * counting is listSummary.js; the status label and colour are the SAME rules the table uses
 * (rowModel statusLabelOf, statusCategory), so the Outcome card and the Status column agree.
 * Plain HTML/CSS bars, no chart library; each bar is role="img" with a sentence, and the legend
 * under it carries every count as text. Empty segments are not drawn (the export's rule).
 */
import { summarizeList } from '@/utils/followups/listSummary.js';
import { statusLabelOf } from '@/utils/followups/rowModel.js';
import { statusCategory } from '@/utils/followups/statusCategory.js';
import { queueByKey } from '@/utils/followups/queues.js';

export default {
  name: 'FollowupsListSummary',
  props: {
    rows: { type: Array, default: () => [] },
    // queues.js key — decides the status label (unscheduled treatment reads "Apt on Schedule").
    queue: { type: String, required: true },
    // The page is still loading its rows: placeholders instead of zeros.
    busy: { type: Boolean, default: false },
    noun: { type: String, default: 'patient' },
  },

  computed: {
    s() {
      const q = queueByKey(this.queue);
      return summarizeList(this.rows, { labelOf: r => statusLabelOf(q, r), categoryOf: statusCategory });
    },

    cards() {
      const attempts = this.s.attempts
        .filter(a => a.count > 0)
        .map(a => ({ label: a.label, count: a.count, cls: `fu-ls__c--${a.key}` }));
      const outcomes = this.s.outcomes
        .map(o => ({ label: o.label, count: o.count, cls: `fu-ls__c--${o.category}` }));
      return [
        { key: 'attempts', title: 'Follow-up attempts', items: attempts, text: this.sentence('Follow-up attempts', attempts) },
        { key: 'outcome', title: 'Outcome', items: outcomes, text: this.sentence('Outcome', outcomes) },
      ];
    },
  },

  methods: {
    sentence(title, items) {
      if (!items.length) return `${title}: no ${this.noun}s on this list.`;
      return `${title}: ${items.map(i => `${i.label} ${i.count}`).join(', ')}.`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-ls {
  @include fu-font-base;
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.1fr) minmax(0, 1.1fr);
  gap: $fu-space-4;
  margin-bottom: $fu-space-4;
}

.fu-ls__card {
  @include fu-card($fu-radius-lg);
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: $fu-space-4 18px;
}

.fu-ls__label {
  @include fu-eyebrow($fu-text-muted);
  margin: 0;
  font-family: $fu-font-sans;
  line-height: 1.4;
}

.fu-ls__value {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px 6px;
  margin-top: 10px;
  min-width: 0;
}

.fu-ls__num {
  @include fu-font-display;
  @include fu-tabular;
  font-weight: 800;
  font-size: 32px;
  line-height: 1;
  letter-spacing: -0.03em;
}

.fu-ls__unit {
  font-size: 14px;
  font-weight: $fu-fw-semibold;
  color: $fu-text-subtle;
}

.fu-ls__progress {
  height: 6px;
  margin-top: $fu-space-3;
  border-radius: 6px;
  background: $fu-neutral-100;
  overflow: hidden;
}

.fu-ls__progress-fill {
  height: 100%;
  border-radius: 6px;
  background: $fu-brand;
  transition: width 0.3s ease;
}

.fu-ls__sub {
  margin-top: 9px;
  font-size: 12.5px;
  color: $fu-text-muted;
}

// The stacked bar: each segment's flex-grow is its count, so the 2px gaps never push it past 100%.
.fu-ls__stack {
  display: flex;
  gap: 2px;
  height: 10px;
  margin-top: 14px;
  border-radius: 6px;
  overflow: hidden;
  background: $fu-neutral-100;
}

.fu-ls__seg {
  flex: 0 1 0;
  min-width: 3px;
  height: 100%;
}

// Before the colour rules below: fu-status-dot paints a grey default that must not win over them.
.fu-ls__dot {
  @include fu-status-dot;
}

// The colour of a segment and its legend dot (both carry the same `fu-ls__c--<key>` class).
// Attempts: grey for none, then darker teal as the count rises (the export's iris 300 / 500 / 700).
// Outcome: the status category colours the table's dots use.
.fu-ls__c {
  @include fu-status-dot-colors;
  &--a0 { background: $fu-neutral-300; }
  &--a1 { background: $fu-brand-300; }
  &--a2 { background: $fu-brand-500; }
  &--a3 { background: $fu-brand-700; }
}

.fu-ls__legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: $fu-space-3 0 0;
  padding: 0;
  list-style: none;
}

.fu-ls__item {
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  min-width: 0;
  font-size: 13px;
}

.fu-ls__item-label {
  @include fu-ellipsis;
  min-width: 0;
  color: $fu-neutral-600;
}

.fu-ls__item-count {
  @include fu-tabular;
  margin-left: auto;
  padding-left: $fu-space-2;
  font-weight: $fu-fw-bold;
}

.fu-ls__empty {
  margin-top: $fu-space-3;
  font-size: 13px;
  color: $fu-text-muted;
}

.fu-ls__sk {
  margin-top: $fu-space-3;
  &--num { @include fu-skeleton(64px, 28px, 8px); }
  &--bar { @include fu-skeleton(100%, 10px, 6px); }
  &--line { @include fu-skeleton(70%, 12px, 6px); }
}

@include fu-tablet {
  .fu-ls { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .fu-ls__card:first-child { grid-column: 1 / -1; }
}

@include fu-phone {
  .fu-ls { grid-template-columns: minmax(0, 1fr); gap: 10px; }
  .fu-ls__card { padding: 14px; }
  .fu-ls__num { font-size: 26px; }
}
</style>
